import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { Trash2, Plus } from 'lucide-react'
import { Input, Button, Switch } from '@teach/ui'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useGetVideoQuery, useSetChaptersMutation, type Chapter } from '@/features/editor/videosApi'
import { secondsToHms, hmsToSeconds, maskMmSs } from '@/features/editor/lib/timecode'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  videoId: string
}

interface Row {
  // Client-only stable key for list rendering; never sent to the backend.
  id: string
  time: string
  title: string
}

/** Тело сохранения строкой — по ней же видно, изменилось ли что-то. */
const payloadOf = (rows: Row[], enabled: boolean): string =>
  JSON.stringify({
    enabled,
    items: rows
      .map((r) => ({ time: hmsToSeconds(r.time), title: r.title.trim() }))
      .filter((r): r is Chapter => r.time !== null && r.title.length > 0)
      .sort((a, b) => a.time - b.time),
  })

export const ChaptersTab = ({ videoId }: Props) => {
  const m = useMessages(messages)
  const { data } = useGetVideoQuery(videoId)
  const [setChapters] = useSetChaptersMutation()

  const seeded = useRef(false)
  const keySeq = useRef(0)
  const nextId = () => `ch-${keySeq.current++}`
  const lastSent = useRef<string | null>(null)
  // Состояние, а не реф: в кадре засева строки ещё старые, и реф пустил бы
  // в сохранение значение ДО засева.
  const [ready, setReady] = useState(false)
  const [rows, setRows] = useState<Row[]>([])
  const [enabled, setEnabled] = useState<boolean>(false)

  useEffect(() => {
    // Засев на первом ответе, даже если глав у ролика ещё нет вовсе.
    if (seeded.current || !data) return
    const seededRows = (data.chapters?.items ?? []).map((c) => ({
      id: nextId(),
      time: secondsToHms(c.time),
      title: c.title,
    }))
    const seededEnabled = data.chapters?.enabled ?? false
    setRows(seededRows)
    setEnabled(seededEnabled)
    // То, что уже на сервере, повторно не шлём.
    lastSent.current = payloadOf(seededRows, seededEnabled)
    seeded.current = true
    setReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i))
  const add = () => setRows((rs) => [...rs, { id: nextId(), time: '', title: '' }])

  // Сохранение само, без кнопки: пауза в правке — и главы уходят на сервер.
  // Неполные строки (без времени или названия) не отправляются, но и не теряются.
  const current = payloadOf(rows, enabled)
  const debounced = useDebouncedValue(current, 800)
  useEffect(() => {
    // `debounced !== current` — пауза ещё не дошла (или это значение ДО засева).
    if (!ready || debounced !== current || debounced === lastSent.current) return
    lastSent.current = debounced
    const body = JSON.parse(debounced) as { enabled: boolean; items: Chapter[] }
    setChapters({ videoId, ...body })
      .unwrap()
      .catch(() => toast.error(messagesNow(messages).chaptersSaveFailed))
  }, [ready, debounced, current, videoId, setChapters])

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={enabled} onCheckedChange={setEnabled} />
        {m.showChapters}
      </label>

      {/* Выключены — ни списка, ни «Добавить главу». */}
      {enabled ? (
        <>
          <div className="space-y-2">
            {rows.map((r, i) => {
              const invalid = r.time.length > 0 && hmsToSeconds(r.time) === null
              return (
                <div key={r.id} className="flex items-center gap-2">
                  <Input
                    value={r.time}
                    onChange={(e) => update(i, { time: maskMmSs(e.target.value) })}
                    inputMode="numeric"
                    placeholder={m.timePlaceholder}
                    aria-invalid={invalid}
                    className={`w-24 ${invalid ? 'border-destructive' : ''}`}
                  />
                  <Input
                    value={r.title}
                    onChange={(e) => update(i, { title: e.target.value })}
                    placeholder={m.chapterTitle}
                    className="flex-1"
                  />
                  <button type="button" aria-label={m.removeChapter} onClick={() => remove(i)}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              )
            })}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={add}>
            <Plus className="mr-1 h-4 w-4" />
            {m.addChapter}
          </Button>
        </>
      ) : null}
    </div>
  )
}
